import React, { useState } from "react";
import VectorMap, { Layer, Legend, Source } from "devextreme-react/vector-map";
import * as mapsData from "devextreme-dist/js/vectormap-data/world.js";

import "./CreditMap.css";

interface GeoRow {
  region: string;
  EL: number;
  EAD: number;
  perc: string;
}

type RegionToCountries = Record<string, string[]>;

const CreditMap: React.FC = () => {
  const [showTable, setShowTable] = useState<boolean>(true);

  // Raw data
  const geoData: GeoRow[] = [
    { region: "Total", EL: 276688, EAD: 50610452, perc: "100%" },
    { region: "Southern Europe", EL: 129814, EAD: 4062591, perc: "8.0%" },
    { region: "Western Europe", EL: 100136, EAD: 45072097, perc: "89.1%" },
    { region: "Brazil", EL: 39672, EAD: 59434, perc: "0.1%" },
    { region: "Singapore", EL: 2687, EAD: 805068, perc: "1.6%" },
    { region: "Eastern Europe", EL: 2534, EAD: 353586, perc: "0.5%" },
    { region: "West Africa", EL: 1654, EAD: 9727, perc: "0.0%" },
    { region: "Central Europe", EL: 100, EAD: 154357, perc: "0.3%" },
    { region: "USA", EL: 39, EAD: 193300, perc: "0.0%" },
    { region: "Japan", EL: 0, EAD: 293, perc: "0.0%" },
    { region: "Northern Europe", EL: 0, EAD: 0, perc: "0.0%" },
    { region: "Canada", EL: 0, EAD: 0, perc: "0.0%" },
    { region: "China", EL: 0, EAD: 0, perc: "0.0%" },
    { region: "Marshall Islands", EL: 0, EAD: 0, perc: "0.0%" },
    { region: "Bermuda", EL: 0, EAD: 0, perc: "0.0%" },
    { region: "UAE", EL: 0, EAD: 0, perc: "0.0%" },
  ];

  // Map your "regions" to actual country names in DevExtreme's world map
  const regionToCountries: RegionToCountries = {
    "Southern Europe": ["Spain", "Italy", "Portugal", "Greece"],
    "Western Europe": ["France", "Germany", "Netherlands", "Belgium", "Switzerland", "Austria"],
    "Eastern Europe": ["Poland", "Czechia", "Slovakia", "Hungary", "Romania", "Bulgaria"],
    "Central Europe": ["Slovenia", "Croatia"],
    "Northern Europe": ["Sweden", "Norway", "Denmark", "Finland"],
    "West Africa": ["Nigeria", "Ghana", "Ivory Coast"],
    Brazil: ["Brazil"],
    Singapore: ["Singapore"],
    USA: ["United States of America"],
    Japan: ["Japan"],
    Canada: ["Canada"],
    China: ["China"],
    "Marshall Islands": ["Marshall Islands"],
    Bermuda: ["Bermuda"],
    UAE: ["United Arab Emirates"],
  };

  // Tooltip function
  const customizeTooltip = (arg: any): { text: string } | null => {
    if (arg.attribute("EAD")) {
      return {
        text: `${arg.attribute("name")}
EAD: ${arg.attribute("EAD").toLocaleString()}
EL: ${arg.attribute("EL").toLocaleString()}`,
      };
    }
    return null;
  };

  return (
    <div className="geo-dashboard">
      <h2 className="geo-title">Geographic Exposure</h2>

      {/* Vector Map */}
      <div className="geo-map-container">
        <VectorMap
          id="vector-map"
          bounds={[-30, 85, 60, 30]}
          tooltip={{ enabled: true, customizeTooltip }}
        >
          <Layer
            dataSource={(mapsData as any).world}
            name="areas"
            colorGroups={[0, 100000, 1000000, 10000000, 50000000]}
            colorGroupingField="EAD"
            palette={["#d0f0c0", "#a8e6cf", "#ffd3b6", "#ffaaa5", "#ff8b94"]}
            customize={(elements: any[]) => {
              elements.forEach((el) => {
                const countryName: string = el.attribute("name");

                // find region that contains this country
                for (const [region, countries] of Object.entries(regionToCountries)) {
                  if (countries.includes(countryName)) {
                    const found = geoData.find((d) => d.region === region);
                    if (found) {
                      el.attribute("EAD", found.EAD);
                      el.attribute("EL", found.EL);
                    }
                  }
                }
              });
            }}
          />
          <Legend>
            <Source layer="areas" grouping="color" />
          </Legend>
        </VectorMap>
      </div>

      {/* By Geography Breakdown */}
      <div className="geo-section">
        <div
          className="geo-section-header"
          onClick={() => setShowTable(!showTable)}
        >
          <h3>Portfolio Breakdown by Geography</h3>
          <span>{showTable ? "\u25B2" : "\u25BC"}</span>
        </div>

        {showTable && (
          <div className="geo-section-content">
            <p className="geo-highlight">
              Geography concentration (HHI): 0.80
            </p>
            <table className="geo-table">
              <thead>
                <tr>
                  <th>Region</th>
                  <th>EL</th>
                  <th>EAD</th>
                  <th>% EAD</th>
                </tr>
              </thead>
              <tbody>
                {geoData.map((row: GeoRow, idx: number) => (
                  <tr key={idx}>
                    <td className={row.region === "Total" ? "geo-bold" : ""}>
                      {row.region}
                    </td>
                    <td>{row.EL.toLocaleString()}</td>
                    <td>{row.EAD.toLocaleString()}</td>
                    <td>{row.perc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreditMap;
